import React, { useState, useEffect } from 'react';
import Api from '../../services/api';
import { initiateRazorpayPayment, processCashPayment } from '../../services/razorpayService';

const PaymentModal = ({ booking, fare, user, onPaymentComplete, onClose }) => {
  const [paymentMethod, setPaymentMethod] = useState('cash'); // 'cash' or 'razorpay'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [razorpayAvailable, setRazorpayAvailable] = useState(true); // Default to true (optimistic)

  // Check Razorpay availability on component mount (non-blocking)
  useEffect(() => {
    const checkRazorpayStatus = async () => {
      try {
        const token = localStorage.getItem('uttaapp_token');
        if (!token) {
          // If no token, keep Razorpay available (will be checked during payment)
          return;
        }

        const response = await fetch(Api.Payment.razorpayStatus, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Only disable if explicitly told it's unavailable
          // If status check fails, keep it available (optimistic approach)
          if (data.razorpayAvailable === false) {
            setRazorpayAvailable(false);
            console.warn('⚠️ Razorpay is not available:', data.message);
          } else {
            setRazorpayAvailable(true);
            console.log('✅ Razorpay is available');
          }
        } else {
          // If status check fails, keep Razorpay available
          // User can try and get proper error message during payment
          console.log('⚠️ Could not check Razorpay status, keeping it available');
        }
      } catch (error) {
        console.error('Error checking Razorpay status:', error);
        // Don't disable Razorpay on error - let user try
        // Error will be shown during actual payment attempt
      }
    };

    checkRazorpayStatus();
  }, []);

  const handlePayment = async () => {
    if (!booking || !fare) {
      setError('Booking information is missing');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const bookingId = booking._id || booking.id;
      const currentUser = user || {
        name: localStorage.getItem('userName') || 'User',
        email: localStorage.getItem('userEmail') || 'user@example.com',
        phone: localStorage.getItem('userPhone') || '9999999999'
      };

      if (paymentMethod === 'cash') {
        // Cash payment - use processCashPayment helper
        try {
          await processCashPayment(bookingId);
          
          console.log('✅ Cash payment completed');
          if (onPaymentComplete) {
            onPaymentComplete({
              paymentMethod: 'cash',
              status: 'completed'
            });
          }
          setLoading(false);
        } catch (error) {
          console.error('Error processing cash payment:', error);
          setError(error.message || 'Failed to process cash payment');
          setLoading(false);
        }
      } else if (paymentMethod === 'razorpay') {
        // Razorpay payment flow - using updated initiateRazorpayPayment
        try {
          await initiateRazorpayPayment(
            fare,
            bookingId,
            currentUser,
            async (paymentResponse) => {
              // Payment successful - updatePaymentStatus is already called inside initiateRazorpayPayment
              console.log('✅ Razorpay payment success:', paymentResponse);
              
              if (onPaymentComplete) {
                onPaymentComplete({
                  paymentMethod: 'razorpay',
                  status: 'completed',
                  transactionId: paymentResponse.razorpay_payment_id
                });
              }
              setLoading(false);
            },
            (error) => {
              // Payment failed or cancelled
              console.error('Payment error:', error);
              const errorMessage = error.message || 'Payment was cancelled or failed';
              
              // If Razorpay is not configured, suggest cash payment
              if (errorMessage.includes('Payment gateway') || 
                  errorMessage.includes('Razorpay') || 
                  errorMessage.includes('authentication') || 
                  errorMessage.includes('configure valid API keys') ||
                  errorMessage.includes('Razorpay authentication failed')) {
                setError('Razorpay payment gateway is not configured. Please use Cash payment to complete your booking, or contact admin to configure Razorpay API keys.');
                // Auto-switch to cash payment option
                setPaymentMethod('cash');
                setRazorpayAvailable(false); // Mark as unavailable
              } else {
                setError(errorMessage);
              }
              setLoading(false);
            }
          );
        } catch (error) {
          console.error('Error initiating Razorpay payment:', error);
          const errorMessage = error.message || 'Failed to initiate payment';
          
          // If Razorpay is not configured, suggest cash payment
          if (errorMessage.includes('Payment gateway') || 
              errorMessage.includes('Razorpay') || 
              errorMessage.includes('authentication') || 
              errorMessage.includes('configure valid API keys') ||
              errorMessage.includes('Razorpay authentication failed')) {
            setError('Razorpay payment gateway is not configured. Please use Cash payment to complete your booking, or contact admin to configure Razorpay API keys.');
            // Auto-switch to cash payment option
            setPaymentMethod('cash');
            setRazorpayAvailable(false); // Mark as unavailable
          } else {
            setError(errorMessage);
          }
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10002] p-4" style={{ display: 'flex' }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-slideUp overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 via-emerald-600 to-green-500 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white">Complete Payment</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
            <p className="text-white text-sm mb-1">Total Amount</p>
            <p className="text-white text-3xl font-black">₹{fare}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Warning Banner - Show only if Razorpay is unavailable */}
          {!razorpayAvailable && (
            <div className="mb-6 border-l-4 p-4 rounded-lg bg-yellow-50 border-yellow-500">
              <div className="flex items-start">
                <div className="flex-shrink-0 text-yellow-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-semibold text-yellow-800">
                    <span className="block mb-1">Online payment is temporarily unavailable. Please select Cash payment below to complete your booking.</span>
                    <span className="text-xs font-normal text-yellow-700 mt-1 block">
                      💡 Tip: Cash payment is available and will complete your booking instantly.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message - Show for other errors */}
          {error && !error.includes('Online payment') && (
            <div className="mb-6 border-l-4 p-4 rounded-lg bg-red-50 border-red-500">
              <div className="flex items-start">
                <div className="flex-shrink-0 text-red-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-semibold text-red-700">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Methods */}
          <div className="space-y-4 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Choose Payment Method</h3>
            
            {/* Cash Option */}
            <button
              onClick={() => setPaymentMethod('cash')}
              disabled={loading}
              className={`w-full p-5 rounded-xl border-2 transition-all duration-200 ${
                paymentMethod === 'cash'
                  ? 'border-green-500 bg-green-50 shadow-lg transform scale-105'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
              } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  paymentMethod === 'cash' ? 'bg-green-500' : 'bg-gray-200'
                }`}>
                  <svg className={`w-6 h-6 ${paymentMethod === 'cash' ? 'text-white' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-bold text-gray-900 text-lg">Cash Payment</h4>
                  <p className="text-sm text-gray-600">Pay directly to the driver</p>
                </div>
                {paymentMethod === 'cash' && (
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
            </button>

            {/* Razorpay Option */}
            <button
              onClick={() => {
                if (!razorpayAvailable || (error && error.includes('Online payment'))) {
                  // Don't allow switching to Razorpay if it's unavailable
                  return;
                }
                setPaymentMethod('razorpay');
                setError(null); // Clear error when switching
              }}
              disabled={loading || !razorpayAvailable || (error && error.includes('Online payment'))}
              className={`w-full p-5 rounded-xl border-2 transition-all duration-200 ${
                paymentMethod === 'razorpay'
                  ? 'border-blue-500 bg-blue-50 shadow-lg transform scale-105'
                  : !razorpayAvailable || (error && error.includes('Online payment'))
                  ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  paymentMethod === 'razorpay' ? 'bg-blue-500' : !razorpayAvailable || (error && error.includes('Online payment')) ? 'bg-gray-300' : 'bg-gray-200'
                }`}>
                  <svg className={`w-6 h-6 ${paymentMethod === 'razorpay' ? 'text-white' : !razorpayAvailable || (error && error.includes('Online payment')) ? 'text-gray-500' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-bold text-gray-900 text-lg">Pay with Razorpay</h4>
                  <p className={`text-sm ${!razorpayAvailable || (error && error.includes('Online payment')) ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                    {!razorpayAvailable || (error && error.includes('Online payment')) ? 'Currently unavailable' : 'Secure online payment'}
                  </p>
                </div>
                {paymentMethod === 'razorpay' && razorpayAvailable && !error && (
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                {(!razorpayAvailable || (error && error.includes('Online payment'))) && (
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-bold text-lg transition-all duration-200"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handlePayment}
              disabled={loading}
              className={`flex-1 px-6 py-4 rounded-xl font-bold text-lg text-white transition-all transform ${
                paymentMethod === 'cash'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                  : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
              } ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 shadow-lg hover:shadow-xl'}`}
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Processing...</span>
                </div>
              ) : (
                paymentMethod === 'cash' ? 'Confirm Cash Payment' : 'Pay with Razorpay'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;

